import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { TrendingUp, TrendingDown, Minus, Plus, Trash2, Search, BarChart3, Phone, AlertTriangle, FileText, Rocket, ExternalLink } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { AuthGate } from "../components/AuthGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LABELS = {
  "/": "Accueil",
  "/blog": "Blog",
  "/galerie": "Galerie",
  "/faq": "FAQ",
  "/temoignages": "Avis clients",
};

function pageLabel(path) {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  return path.replace(/^\//, "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || path;
}

function Evolution({ positions }) {
  if (positions.length < 2) return <span className="text-gray-400 text-sm">—</span>;
  const last = positions[positions.length - 1].position;
  const prev = positions[positions.length - 2].position;
  const delta = prev - last;
  if (delta > 0)
    return (
      <span className="inline-flex items-center space-x-1 text-emerald-600 font-semibold text-sm">
        <TrendingUp className="w-4 h-4" />
        <span>+{delta}</span>
      </span>
    );
  if (delta < 0)
    return (
      <span className="inline-flex items-center space-x-1 text-red-500 font-semibold text-sm">
        <TrendingDown className="w-4 h-4" />
        <span>{delta}</span>
      </span>
    );
  return (
    <span className="inline-flex items-center space-x-1 text-gray-500 text-sm">
      <Minus className="w-4 h-4" />
      <span>stable</span>
    </span>
  );
}

function BoostDelta({ base, current }) {
  if (base == null && current == null) return <span className="text-gray-300 text-sm">—</span>;
  if (base == null) return <span className="text-gray-500 text-sm">non mesuré → #{current}</span>;
  if (current == null) return <span className="text-gray-400 text-sm">#{base} (départ)</span>;
  const diff = base - current;
  if (diff > 0) return <span className="text-emerald-600 text-sm font-semibold">#{base} → #{current} (+{diff})</span>;
  if (diff < 0) return <span className="text-red-500 text-sm font-semibold">#{base} → #{current} ({diff})</span>;
  return <span className="text-gray-500 text-sm">#{base} → #{current} (=)</span>;
}

function PositionBadge({ position }) {
  if (position == null) return <span className="text-gray-400">Non mesuré</span>;
  const color =
    position <= 3 ? "bg-emerald-100 text-emerald-700" : position <= 10 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600";
  return <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm ${color}`}>#{position}</span>;
}

export default function SuiviSEO() {
  return (
    <AuthGate>
      <SuiviSEOInner />
    </AuthGate>
  );
}

function SuiviSEOInner() {
  const [keywords, setKeywords] = useState([]);
  const [clicks, setClicks] = useState({ pages: [], total: 0, last30: 0 });
  const [quotes, setQuotes] = useState({ pages: [], total: 0, last30: 0 });
  const [newKeyword, setNewKeyword] = useState("");
  const [posInputs, setPosInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [baseline, setBaseline] = useState(null);
  const [savingBaseline, setSavingBaseline] = useState(false);

  const load = useCallback(async () => {
    try {
      const [kw, ck, qs, bl] = await Promise.all([
        axios.get(`${API}/seo/keywords`),
        axios.get(`${API}/analytics/call-clicks`),
        axios.get(`${API}/analytics/quote-submits`),
        axios.get(`${API}/seo/baseline`),
      ]);
      setKeywords(kw.data);
      setClicks(ck.data);
      setQuotes(qs.data);
      setBaseline(bl.data && bl.data.date ? bl.data : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addKeyword = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim()) return;
    await axios.post(`${API}/seo/keywords`, { keyword: newKeyword });
    setNewKeyword("");
    load();
  };

  const removeKeyword = async (id) => {
    await axios.delete(`${API}/seo/keywords/${id}`);
    load();
  };

  const addPosition = async (id) => {
    const val = parseInt(posInputs[id], 10);
    if (!val || val < 1) return;
    await axios.post(`${API}/seo/keywords/${id}/positions`, { position: val });
    setPosInputs((p) => ({ ...p, [id]: "" }));
    load();
  };

  const saveBaseline = async () => {
    if (baseline && !window.confirm("Remplacer le point de départ existant ? L'ancienne référence « avant boost » sera perdue.")) return;
    setSavingBaseline(true);
    try {
      await axios.post(`${API}/seo/baseline`);
      await load();
    } catch (e) {
      alert("Erreur lors de l'enregistrement du point de départ. Réessayez.");
    } finally {
      setSavingBaseline(false);
    }
  };

  const baselinePos = (k) => {
    if (!baseline || !baseline.snapshot) return null;
    const entry = baseline.snapshot.find((s) => s.keyword_id === k.id || s.keyword === k.keyword);
    return entry ? entry.position : null;
  };

  const top10 = keywords.filter((k) => k.positions.length && k.positions[k.positions.length - 1].position <= 10).length;
  const mesures = keywords.filter((k) => k.positions.length).length;

  // Rappel visuel : dernière mesure > 30 jours (ou aucune mesure)
  const allDates = keywords.flatMap((k) => k.positions.map((p) => p.date)).sort();
  const lastMeasureDate = allDates.length ? allDates[allDates.length - 1] : null;
  const daysSinceLastMeasure = lastMeasureDate
    ? Math.floor((Date.now() - new Date(lastMeasureDate).getTime()) / 86400000)
    : null;
  const showReminder = !loading && (lastMeasureDate === null || daysSinceLastMeasure > 30);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <Helmet>
        <title>Suivi SEO – Tableau de bord privé | Azur Tech</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />

      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-3 mb-3">
            <BarChart3 className="w-8 h-8 text-emerald-400" />
            <h1 className="text-3xl md:text-4xl font-bold">Suivi de position Google</h1>
          </div>
          <p className="text-gray-300 max-w-2xl">
            Chaque mois, cherchez vos mots-clés sur Google (en navigation privée) et notez la position de azurtech06.fr.
            Vous verrez votre progression au fil du temps.
          </p>
          <a href="/agent-seo" data-testid="link-agent-seo" className="inline-flex items-center space-x-2 mt-4 bg-emerald-500 text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors">
            <span>Ouvrir l'Agent IA Référencement →</span>
          </a>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4">
          {/* Rappel mensuel */}
          {showReminder && (
            <div data-testid="seo-reminder-banner" className="mb-8 bg-amber-50 border border-amber-300 rounded-xl p-5 flex items-start space-x-3 max-w-3xl">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-800">
                  {lastMeasureDate === null
                    ? "Aucune mesure enregistrée pour l'instant"
                    : `Votre dernière mesure date d'il y a ${daysSinceLastMeasure} jours`}
                </p>
                <p className="text-amber-700 text-sm mt-1">
                  Pensez à noter vos positions Google ce mois-ci : cherchez chaque mot-clé en navigation privée et
                  enregistrez la position de azurtech06.fr dans le tableau ci-dessous.
                </p>
              </div>
            </div>
          )}

          {/* Point de départ avant boost */}
          <div data-testid="boost-baseline-card" className={`mb-8 rounded-xl p-5 border max-w-3xl ${baseline ? "bg-emerald-50 border-emerald-300" : "bg-blue-50 border-blue-300"}`}>
            <div className="flex items-start space-x-3">
              <Rocket className={`w-6 h-6 flex-shrink-0 mt-0.5 ${baseline ? "text-emerald-500" : "text-blue-500"}`} />
              <div className="flex-1">
                {baseline ? (
                  <>
                    <p className="font-bold text-emerald-800">Point de départ « avant boost » enregistré le {baseline.date}</p>
                    <p className="text-emerald-700 text-sm mt-1">
                      La colonne « Avant boost » compare vos positions actuelles à ce point de départ pour mesurer l'impact réel du boost de l'agence.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-blue-800">Avant le boost de l'agence : enregistrez votre point de départ</p>
                    <p className="text-blue-700 text-sm mt-1">
                      1. Cliquez sur l'icône <ExternalLink className="w-3.5 h-3.5 inline" /> à côté d'un mot-clé : Google s'ouvre, repérez à quelle position apparaît azurtech06.fr. 2. Saisissez ce numéro dans le tableau. 3. Cliquez sur le bouton ci-dessous : vos positions du jour seront figées comme référence pour mesurer l'impact du boost.
                    </p>
                  </>
                )}
                <button onClick={saveBaseline} disabled={savingBaseline} data-testid="boost-baseline-btn" className={`mt-3 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60 ${baseline ? "bg-emerald-500 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"}`}>
                  {savingBaseline ? "Enregistrement..." : baseline ? "Mettre à jour le point de départ" : "Enregistrer le point de départ (avant boost)"}
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-2xl">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-[#0f172a]" data-testid="seo-stat-total">{keywords.length}</p>
              <p className="text-gray-500 text-sm">Mots-clés suivis</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600" data-testid="seo-stat-top10">{top10}</p>
              <p className="text-gray-500 text-sm">Dans le top 10</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-[#0f172a]" data-testid="seo-stat-mesures">{mesures}</p>
              <p className="text-gray-500 text-sm">Déjà mesurés</p>
            </div>
          </div>

          {/* Ajout mot-clé */}
          <form onSubmit={addKeyword} className="flex gap-3 mb-8 max-w-xl">
            <div className="relative flex-1">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Ajouter un mot-clé (ex: dératisation grasse)"
                data-testid="seo-new-keyword-input"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
              />
            </div>
            <button type="submit" data-testid="seo-add-keyword-btn" className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-5 py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors">
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Ajouter</span>
            </button>
          </form>

          {/* Tableau */}
          {loading ? (
            <p className="text-gray-500">Chargement...</p>
          ) : (
            <>
            {/* Vue mobile : cartes */}
            <div className="md:hidden space-y-4" data-testid="seo-keywords-cards">
              {keywords.map((k) => {
                const last = k.positions.length ? k.positions[k.positions.length - 1] : null;
                return (
                  <div key={k.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2 pr-2">
                        <p className="font-semibold text-[#0f172a]">{k.keyword}</p>
                        <a href={`https://www.google.fr/search?q=${encodeURIComponent(k.keyword)}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 flex-shrink-0">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                      <button onClick={() => removeKeyword(k.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <PositionBadge position={last?.position} />
                      <Evolution positions={k.positions} />
                    </div>
                    {baseline && (
                      <div className="flex items-center justify-between mb-3 text-sm">
                        <span className="text-gray-500">Avant boost</span>
                        <BoostDelta base={baselinePos(k)} current={last?.position} />
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={posInputs[k.id] || ""}
                        onChange={(e) => setPosInputs((p) => ({ ...p, [k.id]: e.target.value }))}
                        placeholder="Nouvelle position"
                        className="flex-1 px-3 py-3 rounded-lg border border-gray-200 focus:border-emerald-500 outline-none text-sm"
                      />
                      <button
                        onClick={() => addPosition(k.id)}
                        className="px-5 py-3 bg-[#0f172a] text-white rounded-lg text-sm font-medium"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vue desktop : tableau */}
            <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[720px]" data-testid="seo-keywords-table">
                <thead>
                  <tr className="bg-gray-50 text-left text-sm text-gray-500 border-b border-gray-200">
                    <th className="px-5 py-4 font-medium">Mot-clé</th>
                    <th className="px-5 py-4 font-medium">Position actuelle</th>
                    <th className="px-5 py-4 font-medium">Évolution</th>
                    <th className="px-5 py-4 font-medium">Avant boost</th>
                    <th className="px-5 py-4 font-medium">Historique</th>
                    <th className="px-5 py-4 font-medium">Nouvelle mesure</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((k) => {
                    const last = k.positions.length ? k.positions[k.positions.length - 1] : null;
                    return (
                      <tr key={k.id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`seo-row-${k.id}`}>
                        <td className="px-5 py-4 font-medium text-[#0f172a]">
                          <div className="flex items-center space-x-2">
                            <span>{k.keyword}</span>
                            <a href={`https://www.google.fr/search?q=${encodeURIComponent(k.keyword)}`} target="_blank" rel="noopener noreferrer" title="Vérifier sur Google" data-testid={`seo-check-google-${k.id}`} className="text-gray-400 hover:text-emerald-600 transition-colors">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </td>
                        <td className="px-5 py-4"><PositionBadge position={last?.position} /></td>
                        <td className="px-5 py-4"><Evolution positions={k.positions} /></td>
                        <td className="px-5 py-4" data-testid={`seo-boost-${k.id}`}><BoostDelta base={baselinePos(k)} current={last?.position} /></td>
                        <td className="px-5 py-4">
                          <div className="flex items-end space-x-1 h-8">
                            {k.positions.slice(-8).map((p, i) => (
                              <div
                                key={i}
                                title={`${p.date} : #${p.position}`}
                                className="w-3 rounded-t bg-emerald-400"
                                style={{ height: `${Math.max(8, 32 - Math.min(p.position, 30))}px` }}
                              ></div>
                            ))}
                            {k.positions.length === 0 && <span className="text-gray-300 text-sm">—</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={posInputs[k.id] || ""}
                              onChange={(e) => setPosInputs((p) => ({ ...p, [k.id]: e.target.value }))}
                              placeholder="#"
                              data-testid={`seo-pos-input-${k.id}`}
                              className="w-16 px-2 py-2 rounded-lg border border-gray-200 focus:border-emerald-500 outline-none text-center"
                            />
                            <button
                              onClick={() => addPosition(k.id)}
                              data-testid={`seo-pos-save-${k.id}`}
                              className="px-3 py-2 bg-[#0f172a] text-white rounded-lg text-sm font-medium hover:bg-[#1e3a5f] transition-colors"
                            >
                              OK
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={() => removeKeyword(k.id)} data-testid={`seo-delete-${k.id}`} className="text-gray-300 hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-5 max-w-3xl">
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong>Comment mesurer ?</strong> Ouvrez Google en navigation privée, tapez le mot-clé, et comptez à quelle
              position apparaît azurtech06.fr (1 = premier résultat). Notez la position ici une fois par mois pour suivre
              votre progression. Cette page est privée et invisible pour Google.
            </p>
          </div>

          {/* Clics sur le numéro d'appel */}
          <div className="mt-12" data-testid="call-clicks-section">
            <div className="flex items-center space-x-3 mb-2">
              <Phone className="w-6 h-6 text-emerald-500" />
              <h2 className="text-2xl font-bold text-[#0f172a]">Clics sur le numéro d'appel</h2>
            </div>
            <p className="text-gray-500 mb-6">
              Chaque fois qu'un visiteur clique sur votre numéro, c'est compté ici, page par page. Vous saurez quelles
              pages vous apportent le plus de clients.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600" data-testid="clicks-stat-30d">{clicks.last30}</p>
                <p className="text-gray-500 text-sm">Clics (30 derniers jours)</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-[#0f172a]" data-testid="clicks-stat-total">{clicks.total}</p>
                <p className="text-gray-500 text-sm">Clics au total</p>
              </div>
            </div>

            {clicks.pages.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-gray-500 max-w-3xl" data-testid="clicks-empty">
                Aucun clic enregistré pour l'instant. Dès qu'un visiteur cliquera sur votre numéro, il apparaîtra ici.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden max-w-3xl">
                <table className="w-full" data-testid="clicks-table">
                  <thead>
                    <tr className="bg-gray-50 text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="px-5 py-4 font-medium">Page</th>
                      <th className="px-5 py-4 font-medium">Clics (30 jours)</th>
                      <th className="px-5 py-4 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clicks.pages.map((p) => (
                      <tr key={p.page} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="px-5 py-4">
                          <p className="font-medium text-[#0f172a]">{pageLabel(p.page)}</p>
                          <p className="text-gray-400 text-xs">{p.page}</p>
                        </td>
                        <td className="px-5 py-4 text-emerald-600 font-semibold">{p.last30}</td>
                        <td className="px-5 py-4 font-semibold">{p.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Demandes de devis */}
          <div className="mt-12" data-testid="quote-submits-section">
            <div className="flex items-center space-x-3 mb-2">
              <FileText className="w-6 h-6 text-emerald-500" />
              <h2 className="text-2xl font-bold text-[#0f172a]">Demandes de devis par formulaire</h2>
            </div>
            <p className="text-gray-500 mb-6">
              Chaque devis envoyé depuis un formulaire du site est compté ici, page par page. Combiné aux clics d'appel,
              vous voyez toutes vos sources de clients.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600" data-testid="quotes-stat-30d">{quotes.last30}</p>
                <p className="text-gray-500 text-sm">Devis (30 derniers jours)</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-[#0f172a]" data-testid="quotes-stat-total">{quotes.total}</p>
                <p className="text-gray-500 text-sm">Devis au total</p>
              </div>
            </div>

            {quotes.pages.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-gray-500 max-w-3xl" data-testid="quotes-empty">
                Aucune demande de devis enregistrée pour l'instant. Dès qu'un visiteur enverra le formulaire, elle apparaîtra ici.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden max-w-3xl">
                <table className="w-full" data-testid="quotes-table">
                  <thead>
                    <tr className="bg-gray-50 text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="px-5 py-4 font-medium">Page</th>
                      <th className="px-5 py-4 font-medium">Devis (30 jours)</th>
                      <th className="px-5 py-4 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.pages.map((p) => (
                      <tr key={p.page} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="px-5 py-4">
                          <p className="font-medium text-[#0f172a]">{pageLabel(p.page)}</p>
                          <p className="text-gray-400 text-xs">{p.page}</p>
                        </td>
                        <td className="px-5 py-4 text-emerald-600 font-semibold">{p.last30}</td>
                        <td className="px-5 py-4 font-semibold">{p.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
